/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EELinkPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, IconButton, Stack, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import ComponentLinkTemplate from "./ComponentLinkTemplate";
import CustomToolTip from "../../../infoButton/CustomToolTip";
import CustomSearchbarWithApiV2 from "../../common/searchBar/CustomSearchbarWithApiV2";
import { componentStyle } from "../../../../config/appTheme";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import AISuggestionDialogContainer from "../../SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import { ENTITY } from "../../SmartMatchingDialogContainer/constants/entityTypes";
import RecreateLinkDialog from "./RecreateLinkDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModuleViewMoreDialog from "../../dashboard/pages/tickets/ModuleViewMoreDialog";

function LeftCardActionIcons({
  item,
  actionTypeTooltip,
  onActionTypeClick,
  onRecreateDialogOpen,
  onSmartMatchRequest,
  onViewButtonClick,
}) {
  const actionType = Number(item?.action_type);
  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 2,
          right: 2,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {[1, 2, 4, 5].includes(actionType) && (
          <CustomToolTip type="component" title={actionTypeTooltip}>
            <IconButton
              size="small"
              sx={{ color: "black", p: 0.5 }}
              onClick={(event) => onActionTypeClick(event, item)}
            >
              <ErrorOutlineIcon sx={{ fontSize: "15px" }} />
            </IconButton>
          </CustomToolTip>
        )}
        {item?.parent_ee_id && (
          <CustomToolTip type="component" title="Recreate Link">
            <IconButton
              size="small"
              sx={{ color: "black" }}
              onClick={(e) => {
                e.stopPropagation();
                onRecreateDialogOpen(item);
              }}
            >
              <RefreshIcon sx={{ fontSize: "15px", color: "black" }} />
            </IconButton>
          </CustomToolTip>
        )}
        <CustomToolTip type="component" title="Initiate Smart-matching">
          <IconButton
            size="small"
            sx={{ color: "black" }}
            onClick={(e) => {
              e.stopPropagation();
              onSmartMatchRequest(item);
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: "15px", color: "black" }} />
          </IconButton>
        </CustomToolTip>
        <IconButton
          size="small"
          sx={{ color: "#000000", p: 0.5 }}
          onClick={() => onViewButtonClick(item)}
        >
          <VisibilityIcon sx={{ fontSize: "15px", color: "black" }} />
        </IconButton>
      </Box>
    </Box>
  );
}

LeftCardActionIcons.propTypes = {
  item: PropTypes.object,
  actionTypeTooltip: PropTypes.string,
  onActionTypeClick: PropTypes.func,
  onRecreateDialogOpen: PropTypes.func,
  onSmartMatchRequest: PropTypes.func,
  onViewButtonClick: PropTypes.func,
};

function RightCardActionIcons({
  item,
  selectedMainEEDetails,
  onUnlink,
  onOpenNetworkInterface,
}) {
  if (!selectedMainEEDetails) return null;
  const isLinked = item?.linked_ee_ids?.includes(selectedMainEEDetails?.ee_id);
  return (
    <Box sx={{ position: "relative" }}>
      <CustomToolTip
        type="component"
        title={isLinked ? "Unlink" : "Link"}
      >
        <IconButton
          size="small"
          sx={{
            fontSize: "12px",
            color: "black",
            position: "absolute",
            top: 2,
            right: 2,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isLinked) {
              onUnlink(item);
            } else {
              onOpenNetworkInterface(item);
            }
          }}
        >
          {isLinked ? (
            <RemoveCircleOutlineIcon sx={{ fontSize: 15 }} />
          ) : (
            <ControlPointIcon sx={{ fontSize: 15 }} />
          )}
        </IconButton>
      </CustomToolTip>
    </Box>
  );
}

RightCardActionIcons.propTypes = {
  item: PropTypes.object,
  selectedMainEEDetails: PropTypes.object,
  onUnlink: PropTypes.func,
  onOpenNetworkInterface: PropTypes.func,
};

function formatEEComponentCards(list) {
  return list.map((item) => ({
    data: item,
    id: item.ee_id,
    cardDataArray: [
      {
        key: "Assembly No",
        value: (
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
            title={`Assembly No: ${item?.assembly_number ?? ""}`}
          >
            {`${item?.assembly_number ?? ""}`}
          </CustomToolTip>
        ),
        keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
        valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
      },
      {
        key: "Version",
        value: (
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
            title={`Version: ${item?.version ?? ""}`}
          >
            {`${item?.version ?? ""}`}
          </CustomToolTip>
        ),
      },
      {
        key: "Description",
        value: (
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
            title={`Description: ${item?.description ?? ""}`}
          >
            {`${item?.description ?? ""}`}
          </CustomToolTip>
        ),
      },
    ],
  }));
}

function formatEELinkedComponentCards(list, selectedMainEEId) {
  return list
    .filter((item) => item.ee_id !== selectedMainEEId)
    .map((item) => {
      const isLinked = item?.linked_ee_ids?.includes(selectedMainEEId);
      return {
        data: item,
        id: item.ee_id,
        isLinked: isLinked,
        cardStyle: isLinked ? { border: "1px solid #4CAF50" } : {},
        cardDataArray: [
          {
            key: "Assembly No",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Assembly No: ${item?.assembly_number ?? ""}`}
              >
                {`${item?.assembly_number ?? ""}`}
              </CustomToolTip>
            ),
            keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
            valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
          },
          {
            key: "Version",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Version: ${item?.version ?? ""}`}
              >
                {`${item?.version ?? ""}`}
              </CustomToolTip>
            ),
          },
          {
            key: "Description",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Description: ${item?.description ?? ""}`}
              >
                {`${item?.description ?? ""}`}
              </CustomToolTip>
            ),
          },
        ],
      };
    });
}

function getEEActionTypeTooltip(actionType) {
  switch (Number(actionType)) {
    case 1:
      return "Action Pending in EE Component to EE Component linking";
    case 2:
      return "Action Pending in EE Component to Legal linking";
    case 4:
      return "Action required in EE Component Attribute";
    case 5:
      return "Action pending in EE Component Version";
    default:
      return "";
  }
}

function getZoneOptionsList(zonesList) {
  if (!Array.isArray(zonesList) || zonesList.length === 0) return [];
  return zonesList
    .map((el) => ({
      value: el?.position,
      label: el?.zone_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function formatParentEELinkRows(responseList) {
  if (!Array.isArray(responseList)) return [];
  return responseList.map((ee) => ({
    id: ee.ee_id,
    component: {
      primary: {
        key: "Number:",
        value: ee.assembly_number,
      },
      secondary: [
        { key: "version:", value: ee.version },
        { key: "Description:", value: ee.description },
      ],
    },
    raw: ee,
  }));
}

function getFirstItemIfValid(resp) {
  if (!resp?.error && Array.isArray(resp?.data) && resp.data.length > 0) {
    return resp.data[0];
  }
  return null;
}

function buildEESWMQueryParams({
  selectedEE,
  selectedZoneMain,
  selectedZoneDetails,
  leftPage,
}) {
  const params = new URLSearchParams();
  if (selectedEE?.ee_id) {
    params.set("ee_id", selectedEE.ee_id);
  }
  params.set("actionMode", "eeComponentAction");
  if (selectedZoneMain) {
    params.set(
      "selectedZone",
      selectedZoneDetails?.zone_name || selectedZoneMain,
    );
    params.set("zonePosition", selectedZoneMain);
  }
  if (leftPage) {
    params.set("page", leftPage);
  }
  return params.toString();
}

function getSearchbarParams({
  projectID,
  variantID,
  selectedProjectIsZonal,
  zonePosition,
  ee_idFromUrl,
  page,
  rowsPerPage,
}) {
  if (!projectID || !variantID) return {};
  return {
    project_id: Number(projectID),
    variant_id: Number(variantID),
    ...(selectedProjectIsZonal && zonePosition
      ? { zone_position: zonePosition }
      : {}),
    ...(ee_idFromUrl ? { ee_id: Number(ee_idFromUrl) } : {}),
    status: true,
    archive: false,
    page_number: page,
    page_size: rowsPerPage,
  };
}

function EELinkPage({ onBeforeSmartMatch }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );

  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const { networkInterfacesEE } = useSelector(
    (state) => state.networkInterfacesReducer,
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const zoneFromurl = queryParams.get("selectedZone");
  const ee_idFromUrl = queryParams.get("ee_id");

  const [eeComponentsMainList, setEEComponentsMainList] = useState([]);
  const [eeMainCount, setEEMainCount] = useState(0);
  const [eeLinkCount, setEELinkCount] = useState(0);
  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [selectedZoneMain, setSelectedZoneMain] = useState(null);
  const [selectedZoneLinked, setSelectedZoneLinked] = useState(null);
  const [leftPage, setLeftPage] = useState(1);
  const [rightPage, setRightPage] = useState(1);
  const rowsPerPage = 25;
  const [zoneMainOptionsList, setZoneMainOptionsList] = useState([]);
  const [zoneLinkedOptionsList, setZoneLinkedOptionsList] = useState([]);
  const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
  const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
  const [modifiedEEComponentList, setModifiedEEComponentList] = useState([]);
  const [modifiedEEComponentLinkList, setModifiedEEComponentLinkList] =
    useState([]);
  const [selectedMainEEDetails, setSelectedMainEEDetails] = useState(null);
  const [eeComponentsLinkedList, setEEComponentsLinkedList] = useState([]);
  const selectedEELinkedListRef = useRef([]);
  const [selectedNetworkItem, setSelectedNetworkItem] = useState(null);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [selectedNetworkInterface, setSelectedNetworkInterface] =
    useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const [isEEDialogOpen, setIsEEDialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [pendingEEActionDialogOpen, setPendingEEActionDialogOpen] =
    useState(false);
  const [selectedPendingEEAction, setSelectedPendingEEAction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [eeGetTriggerMain] = useState(false);
  const eeRequestCounter = useRef(0);
  const leftZoneTimerRef = useRef(null);
  const rightZoneTimerRef = useRef(null);

  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const [openEELinkRecreateDialog, setOpenEELinkRecreateDialog] =
    useState(false);
  const [recreateEELinkList, setRecreateEELinkList] = useState([]);
  const [selectedRecreateEEForLink, setSelectedRecreateEEForLink] =
    useState(null);

  const handleSmartMatchRequest = useCallback((componentDetails) => {
    const componentName = componentDetails?.description;
    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    }
    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source: [
        {
          entityType: ENTITY.TYPE.EE,
          entityId: componentDetails?.ee_id,
        },
      ],
      targetEntityTypes: [ENTITY.TYPE.EE],
      topK: 5,
      minConfidence: 0.1,
      description: componentName,
    });
  }, []);

  const getEEMainComponentsList = useCallback(
    ({
      projectId,
      zonePosition,
      variantId,
      pageNo = 1,
      pageSize = rowsPerPage,
    }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: projectId,
            status: true,
            archive: false,
            variant_id: variantId,
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsMainList([]);
          setEEMainCount(0);
        } else {
          setEEComponentsMainList(resp?.data || []);
          setEEMainCount(resp?.total_count || resp?.data?.length || 0);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const getEEIntersectionsList = useCallback(
    ({ projectId, eeId, variantId }) => {
      if (!projectId || !eeId) return Promise.resolve(null);

      return dispatch(
        HandleApiActions({
          ...labCar.get_ee_intersections,
          params: {
            project_id: Number(projectId),
            status: true,
            variant_id: Number(variantId),
            ee_id: Number(eeId),
          },
          show_toast: false,
        }),
      ).then((resp) => {
        selectedEELinkedListRef.current = resp?.error ? [] : resp?.data || [];
        return resp;
      });
    },
    [dispatch],
  );

  const handleRecreateDialogOpen = useCallback(
    (eeDetails) => {
      return dispatch(
        HandleApiActions({
          ...labCar.get_parent_ee_link,
          params: {
            project_id: selectedProjectDetails?.project_id,
            parent_project_id: selectedProjectDetails?.parent_project_id,
            ee_id: eeDetails?.ee_id,
            parent_ee_id: eeDetails?.parent_ee_id,
          },
        }),
      ).then((parentEELinkResponse) => {
        if (
          parentEELinkResponse?.error ||
          !Array.isArray(parentEELinkResponse?.data)
        ) {
          setRecreateEELinkList([]);
        } else {
          setRecreateEELinkList(
            formatParentEELinkRows(parentEELinkResponse.data),
          );
        }
        setOpenEELinkRecreateDialog(true);
      });
    },
    [dispatch, selectedProjectDetails],
  );

  const getEELinkedComponentsList = useCallback(
    ({
      projectId,
      zonePosition,
      variantId,
      pageNo = 1,
      pageSize = rowsPerPage,
    }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: projectId,
            status: true,
            archive: false,
            variant_id: variantId,
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsLinkedList([]);
          setEELinkCount(0);
        } else {
          setEEComponentsLinkedList(resp?.data || []);
          setEELinkCount(resp?.total_count || resp?.data?.length || 0);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const getEEMainLinkedComponentsList = useCallback(
    ({ projectId, variantId, pageNo = 1, pageSize = rowsPerPage }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      return dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            archive: false,
            project_id: projectId,
            variant_id: variantId,
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      ).then((resp) => {
        if (reqId !== eeRequestCounter.current) return resp;

        if (resp?.error) {
          setEEComponentsMainList([]);
          setEEComponentsLinkedList([]);
          setEEMainCount(0);
          setEELinkCount(0);
        } else {
          setEEComponentsMainList(resp?.data || []);
          setEEComponentsLinkedList(resp?.data || []);
          setEEMainCount(resp?.total_count || resp?.data?.length || 0);
          setEELinkCount(resp?.total_count || resp?.data?.length || 0);
        }
        return resp;
      });
    },
    [dispatch],
  );

  const refreshComponentLists = useCallback(() => {
    const variantId = selectedSingleVariantDetails?.variant_id;
    if (!projectID || !variantId) return;

    if (selectedProjectIsZonal) {
      if (selectedZoneMain) {
        getEEMainComponentsList({
          variantId,
          projectId: projectID,
          zonePosition: selectedZoneMain,
        });
      }
      if (selectedZoneLinked) {
        getEELinkedComponentsList({
          projectId: projectID,
          variantId,
          zonePosition: selectedZoneLinked,
        });
      }
    } else {
      getEEMainLinkedComponentsList({
        projectId: projectID,
        variantId,
      });
    }

    if (selectedMainEEDetails?.ee_id) {
      getEEIntersectionsList({
        variantId: Number(variantId),
        projectId: Number(projectID),
        eeId: Number(selectedMainEEDetails?.ee_id),
      });
    }
  }, [
    projectID,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectIsZonal,
    selectedZoneMain,
    selectedZoneLinked,
    selectedMainEEDetails?.ee_id,
    getEEMainComponentsList,
    getEELinkedComponentsList,
    getEEMainLinkedComponentsList,
    getEEIntersectionsList,
  ]);

  const closeRecreateDialog = () => {
    setOpenEELinkRecreateDialog(false);
    refreshComponentLists();
  };

  const handleOnClickRecreateLink = (selectedEE) => {
    setNetworkDialogOpen(true);
    setSelectedRecreateEEForLink(selectedEE);
    setSelectedNetworkInterface(selectedEE?.parent_network_interface);
  };

  const handleRecreateLink = () => {
    const reqObj = {
      child: {
        ee_id: selectedRecreateEEForLink?.ee_id,
      },
      current_time: new Date().toISOString(),
      module_type: "ecu",
      network_interface: selectedNetworkInterface,
      parent: { ee_id: selectedMainEEDetails?.ee_id },
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      parent_parent_ee_id: selectedMainEEDetails?.parent_ee_id,
      parent_child_ee_id: selectedRecreateEEForLink?.parent_ee_id,
      session_id: selectedRecreateEEForLink?.session_id,
    };

    return dispatch(
      HandleApiActions({
        ...labCar.recreate_ee_link,
        params: reqObj,
        show_toast: true,
      }),
    ).then((resp) => {
      if (!resp?.error) {
        setNetworkDialogOpen(false);
        handleRecreateDialogOpen(selectedMainEEDetails);
      }
    });
  };

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_ee_network_interfaces,
        params: {
          network_type: "ecu",
        },
        show_toast: false,
      }),
    );
  }, [dispatch]);

  const handleOpenNetworkInterface = useCallback((item) => {
    setSelectedNetworkItem(item);
    setNetworkDialogOpen(true);
  }, []);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const updateChangesAndClose = () => {
    handleDialogClose();
    navigate(`/dashboard/v2/link?selectedZone=${selectedZoneMain}`);
  };

  const confirmUpdateAndRedirect = () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(projectID),
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: Number(selectedMainEEDetails?.ee_id),
      is_dependency_linking: true,
      module_type: "LINKING",
    };
    return dispatch(
      HandleApiActions({
        ...labCar.update_action_type,
        params: reqObj,
        show_toast: false,
      }),
    )
      .then(() => {
        navigate(`/dashboard/v2/link?selectedZone=${selectedZoneMain}`);
        handleDialogClose();
        handleActionType2DialogOpen();
      })
      .catch((error) => {
        console.error("Failed to update action type", error);
      });
  };

  const handleEEComponentLink = (selectedLinkedEEDetails) => {
    const variantId = selectedSingleVariantDetails?.variant_id;
    if (
      projectID &&
      selectedMainEEDetails?.ee_id &&
      selectedLinkedEEDetails?.ee_id &&
      variantId
    ) {
      let currentTime = new Date().toISOString();

      dispatch(
        HandleApiActions({
          ...labCar.link_ee_component,
          params: {
            project_id: Number(projectID),
            variant_id: Number(variantId),
            parent: {
              ee_id: Number(selectedMainEEDetails?.ee_id),
            },
            child: {
              ee_id: Number(selectedLinkedEEDetails?.ee_id),
            },
            current_time: currentTime,
            network_interface: selectedNetworkInterface,
            module_type: "ecu",
          },
          show_toast: true,
        }),
      ).then(() => {
        refreshComponentLists();
      });
    }
  };

  const handleCloseNetworkInterface = () => {
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  const handleActionType2DialogOpen = () => {
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handlePendingEEActionDialogClose = () => {
    setPendingEEActionDialogOpen(false);
    setSelectedPendingEEAction(null);
  };

  const handleRedirectionToTypeApproval = () => {
    navigate(
      `/dashboard/v2/type-approval/link?ee_id=${selectedMainEEDetails?.ee_id}&zone=${selectedZoneMain}&tab=2`,
    );
    setActionType2DialogOpen(false);
  };

  const handleRedirectionToEEComponents = () => {
    const selectedEE = selectedPendingEEAction || selectedMainEEDetails;
    const selectedZoneDetails = selectedProjectZonesList?.find(
      (zone) => Number(zone?.position) === Number(selectedZoneMain),
    );
    const queryString = buildEESWMQueryParams({
      selectedEE,
      selectedZoneMain,
      selectedZoneDetails,
      leftPage,
    });

    navigate(`/dashboard/v2/EESWM?${queryString}`);
    handlePendingEEActionDialogClose();
  };

  const toggleEEDialog = useCallback(
    (newOpen) => () => {
      setIsEEDialogOpen(newOpen);
    },
    [],
  );

  const handleEEViewButtonClick = useCallback(
    (item) => {
      setSelectedComponent({ ee_details: item });
      setApiAction(labCar.get_ee_intersections);
      setLinkApiProps({
        ee_id: item?.ee_id,
        archive: false,
      });
      setApiDataKey("linked_ee_details");
      setModuleViewMoreType("EE");
      toggleEEDialog(true)();
    },
    [toggleEEDialog],
  );

  const handleEEComponentUnLink = useCallback(
    (selectedLinkedEEDetails) => {
      const variantId = selectedSingleVariantDetails?.variant_id;
      if (
        projectID &&
        selectedMainEEDetails?.ee_id &&
        selectedLinkedEEDetails?.ee_id &&
        variantId
      ) {
        let currentTime = new Date().toISOString();

        dispatch(
          HandleApiActions({
            ...labCar.unlink_ee_component,
            params: {
              project_id: Number(projectID),
              variant_id: Number(variantId),
              ee_id: Number(selectedMainEEDetails?.ee_id),
              removing_ee_id: Number(selectedLinkedEEDetails?.ee_id),
              current_time: currentTime,
            },
            show_toast: true,
          }),
        ).then(() => {
          refreshComponentLists();
        });
      }
    },
    [
      projectID,
      selectedMainEEDetails?.ee_id,
      selectedSingleVariantDetails?.variant_id,
      dispatch,
      refreshComponentLists,
    ],
  );

  const handleNetworkInterfaceSelect = (item) => {
    if (openEELinkRecreateDialog) {
      handleRecreateLink();
    } else {
      handleEEComponentLink(item);
    }
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  useEffect(() => {
    setProjectID(selectedProjectDetails?.project_id);
    setDialogOpen(false);
    setComponentToBeMatched([]);
  }, [selectedProjectDetails]);

  useEffect(() => {
    setVariantID(selectedSingleVariantDetails?.variant_id);
    setDialogOpen(false);
    setComponentToBeMatched([]);
  }, [selectedSingleVariantDetails]);

  useEffect(() => {
    setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
    setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
  }, [selectedProjectDetails]);

  useEffect(() => {
    if (!selectedProjectIsZonal) {
      setRightPage(leftPage);
    }
  }, [selectedProjectIsZonal, leftPage]);

  useEffect(() => {
    if (!selectedProjectIsZonal || !selectedProjectZonesList?.length) return;

    const filteredOptions = getZoneOptionsList(selectedProjectZonesList);
    setZoneMainOptionsList(filteredOptions);
    setZoneLinkedOptionsList(filteredOptions);

    const zoneFromUrlValue = filteredOptions.find(
      (zone) => zone.label === zoneFromurl || zone.value === zoneFromurl,
    );

    if (zoneFromUrlValue) {
      setSelectedZoneMain(zoneFromUrlValue.value);
      setSelectedZoneLinked(zoneFromUrlValue.value);
    } else if (filteredOptions[0]?.value) {
      setSelectedZoneMain(filteredOptions[0].value);
      setSelectedZoneLinked(filteredOptions[0].value);
    }
  }, [selectedProjectZonesList, zoneFromurl, selectedProjectIsZonal]);

  const prevLeftZoneKeyRef = useRef(null);
  useEffect(() => {
    if (!selectedProjectIsZonal) return;

    const zoneKey = `${projectID || ""}|${variantID || ""}|${
      selectedZoneMain || ""
    }`;
    const zoneChanged = prevLeftZoneKeyRef.current !== zoneKey;
    prevLeftZoneKeyRef.current = zoneKey;

    if (zoneChanged) {
      setSelectedMainEEDetails(null);
      if (leftPage !== 1) setLeftPage(1);
    }

    if (leftZoneTimerRef.current) clearTimeout(leftZoneTimerRef.current);
    if (projectID && variantID && selectedZoneMain) {
      const effectivePage = zoneChanged ? 1 : leftPage;
      leftZoneTimerRef.current = setTimeout(() => {
        getEEMainComponentsList({
          projectId: projectID,
          variantId: variantID,
          zonePosition: selectedZoneMain,
          pageNo: effectivePage,
          pageSize: rowsPerPage,
        }).then((resp) => {
          if (zoneChanged) {
            const firstItem = getFirstItemIfValid(resp);
            if (firstItem) setSelectedMainEEDetails(firstItem);
          }
        });
      }, 250);
    }

    return () => {
      if (leftZoneTimerRef.current) {
        clearTimeout(leftZoneTimerRef.current);
        leftZoneTimerRef.current = null;
      }
    };
  }, [
    selectedZoneMain,
    selectedProjectIsZonal,
    projectID,
    variantID,
    leftPage,
    rowsPerPage,
    getEEMainComponentsList,
  ]);

  const prevRightZoneKeyRef = useRef(null);
  useEffect(() => {
    if (!selectedProjectIsZonal) return;

    const zoneKey = `${projectID || ""}|${variantID || ""}|${
      selectedZoneLinked || ""
    }`;
    const zoneChanged = prevRightZoneKeyRef.current !== zoneKey;
    prevRightZoneKeyRef.current = zoneKey;

    if (zoneChanged && rightPage !== 1) setRightPage(1);

    if (rightZoneTimerRef.current) clearTimeout(rightZoneTimerRef.current);
    if (projectID && variantID && selectedZoneLinked) {
      const effectivePage = zoneChanged ? 1 : rightPage;
      rightZoneTimerRef.current = setTimeout(() => {
        getEELinkedComponentsList({
          projectId: projectID,
          variantId: variantID,
          zonePosition: selectedZoneLinked,
          pageNo: effectivePage,
          pageSize: rowsPerPage,
        });
      }, 250);
    }

    return () => {
      if (rightZoneTimerRef.current) {
        clearTimeout(rightZoneTimerRef.current);
        rightZoneTimerRef.current = null;
      }
    };
  }, [
    selectedZoneLinked,
    selectedProjectIsZonal,
    projectID,
    variantID,
    rightPage,
    rowsPerPage,
    getEELinkedComponentsList,
  ]);

  useEffect(() => {
    if (!selectedProjectIsZonal) {
      setSelectedMainEEDetails(null);
    }
  }, [selectedProjectIsZonal]);

  const prevCentralKeyRef = useRef(null);
  useEffect(() => {
    if (selectedProjectIsZonal || !projectID || !variantID) return;

    const centralKey = `${projectID}|${variantID}`;
    const keyChanged = prevCentralKeyRef.current !== centralKey;
    prevCentralKeyRef.current = centralKey;

    if (keyChanged && leftPage !== 1) setLeftPage(1);

    const effectivePage = keyChanged ? 1 : leftPage;
    getEEMainLinkedComponentsList({
      projectId: projectID,
      variantId: variantID,
      pageNo: effectivePage,
      pageSize: rowsPerPage,
    }).then((resp) => {
      if (keyChanged || !selectedMainEEDetails) {
        const firstItem = getFirstItemIfValid(resp);
        if (firstItem) setSelectedMainEEDetails(firstItem);
      }
    });
  }, [
    selectedProjectIsZonal,
    projectID,
    variantID,
    leftPage,
    rowsPerPage,
    getEEMainLinkedComponentsList,
    selectedMainEEDetails,
  ]);

  useEffect(() => {
    if (projectID && selectedMainEEDetails?.ee_id && variantID) {
      getEEIntersectionsList({
        variantId: Number(variantID),
        projectId: Number(projectID),
        eeId: Number(selectedMainEEDetails?.ee_id),
      });
    }
  }, [selectedMainEEDetails, projectID, variantID, getEEIntersectionsList]);

  useEffect(() => {
    setModifiedEEComponentLinkList(
      formatEELinkedComponentCards(
        eeComponentsLinkedList,
        selectedMainEEDetails?.ee_id,
      ),
    );
  }, [eeComponentsLinkedList, selectedMainEEDetails?.ee_id]);

  useEffect(() => {
    const modifiedList = formatEEComponentCards(eeComponentsMainList);
    setModifiedEEComponentList(modifiedList);
    if (modifiedList.length && !selectedMainEEDetails) {
      setSelectedMainEEDetails(modifiedList[0].data);
    }
  }, [eeComponentsMainList, selectedMainEEDetails]);

  const handleEEActionTypeClick = useCallback(
    (event, item) => {
      event.stopPropagation();
      setSelectedMainEEDetails(item);

      if (Number(item?.action_type) === 1) {
        handleDialogOpen();
      } else if (Number(item?.action_type) === 2) {
        handleActionType2DialogOpen();
      } else if ([4, 5].includes(Number(item?.action_type))) {
        setSelectedPendingEEAction(item);
        setPendingEEActionDialogOpen(true);
      }
    },
    [],
  );

  const renderLeftCardActionIcons = useCallback(
    (item) => (
      <LeftCardActionIcons
        item={item}
        actionTypeTooltip={getEEActionTypeTooltip(Number(item?.action_type))}
        onActionTypeClick={handleEEActionTypeClick}
        onRecreateDialogOpen={(ee) => {
          setSelectedMainEEDetails(ee);
          handleRecreateDialogOpen(ee);
        }}
        onSmartMatchRequest={(ee) => {
          setSelectedMainEEDetails(ee);
          handleSmartMatchRequest(ee);
        }}
        onViewButtonClick={handleEEViewButtonClick}
      />
    ),
    [
      handleEEActionTypeClick,
      handleRecreateDialogOpen,
      handleSmartMatchRequest,
      handleEEViewButtonClick,
    ],
  );

  const renderRightCardActionIcons = useCallback(
    (item) => (
      <RightCardActionIcons
        item={item}
        selectedMainEEDetails={selectedMainEEDetails}
        onUnlink={handleEEComponentUnLink}
        onOpenNetworkInterface={handleOpenNetworkInterface}
      />
    ),
    [
      selectedMainEEDetails,
      handleEEComponentUnLink,
      handleOpenNetworkInterface,
    ],
  );

  return (
    <Box>
      <ComponentLinkTemplate
        leftCardHeader={
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Typography>Select Component</Typography>
            </Box>
            <Box
              display="flex"
              gap={1}
              sx={{
                width: "100%",
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomSearchbarWithApiV2
                  key={`ee-search-${projectID}-${variantID}-${selectedZoneMain}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"assembly_number"}
                  placeholder={"Search Assembly Number"}
                  triggerApi={eeGetTriggerMain}
                  getDataOnSearch={(data, resp) => {
                    if (resp?.error) {
                      setEEComponentsMainList([]);
                    } else {
                      setEEComponentsMainList(resp?.data || []);
                      setEEMainCount(
                        resp?.total_count || resp?.data?.length || 0,
                      );
                    }
                  }}
                  skipApiCall={
                    !projectID ||
                    !variantID ||
                    (selectedProjectIsZonal && !selectedZoneMain)
                  }
                  skipInitialSearch={true}
                  params={getSearchbarParams({
                    projectID,
                    variantID,
                    selectedProjectIsZonal,
                    zonePosition: selectedZoneMain,
                    ee_idFromUrl,
                    page: leftPage,
                    rowsPerPage,
                  })}
                  iconColor={"white"}
                  txtFldStyle={componentStyle.searchField.textField}
                />
              </Box>
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 38%" }}>
                  <CustomSelectV2
                    label={"Zone"}
                    optionsList={zoneMainOptionsList}
                    selectedValue={selectedZoneMain}
                    handleOnChange={(e) => setSelectedZoneMain(e.target.value)}
                    dark={true}
                    showLabel={false}
                  />
                </Box>
              )}
            </Box>
          </Box>
        }
        leftDataList={modifiedEEComponentList}
        leftHandleSelectedComponent={(selectedObj) => {
          setSelectedMainEEDetails(selectedObj.data);
        }}
        leftSelectedComponentDetails={selectedMainEEDetails}
        leftRenderCardActionIcons={renderLeftCardActionIcons}
        leftPage={leftPage}
        leftRowsPerPage={rowsPerPage}
        leftHandlePageChange={(leftPage) => {
          setLeftPage(leftPage);
        }}
        leftTotalCount={eeMainCount}
        leftCardID={"ee_id"}
        rightCardHeader={
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
            >
              <Typography>Link to Component</Typography>
            </Box>
            <Box
              display="flex"
              gap={1}
              sx={{
                width: "100%",
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomSearchbarWithApiV2
                  key={`ee-search-${projectID}-${variantID}-${selectedZoneMain}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"assembly_number"}
                  placeholder={"Search Assembly Number"}
                  getDataOnSearch={(data, resp) => {
                    if (resp?.error) {
                      setEEComponentsLinkedList([]);
                    } else {
                      setEEComponentsLinkedList(resp?.data || []);
                      setEELinkCount(
                        resp?.total_count || resp?.data?.length || 0,
                      );
                    }
                  }}
                  skipApiCall={
                    !projectID ||
                    !variantID ||
                    (selectedProjectIsZonal && !selectedZoneLinked)
                  }
                  skipInitialSearch={true}
                  params={getSearchbarParams({
                    projectID,
                    variantID,
                    selectedProjectIsZonal,
                    zonePosition: selectedZoneLinked,
                    page: selectedProjectIsZonal ? rightPage : leftPage,
                    rowsPerPage,
                  })}
                  iconColor={"white"}
                  txtFldStyle={componentStyle.searchField.textField}
                />
              </Box>
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 38%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneLinkedOptionsList}
                    selectedValue={selectedZoneLinked}
                    handleOnChange={(e) =>
                      setSelectedZoneLinked(e.target.value)
                    }
                    dark={true}
                    showLabel={false}
                  />
                </Box>
              )}
            </Box>
          </Box>
        }
        rightDataList={modifiedEEComponentLinkList}
        rightPage={selectedProjectIsZonal ? rightPage : leftPage}
        rightRowsPerPage={rowsPerPage}
        rightHandlePageChange={(nextPage) => {
          if (selectedProjectIsZonal) {
            setRightPage(nextPage);
          } else {
            setLeftPage(nextPage);
          }
        }}
        rightTotalCount={eeLinkCount}
        rightCardStyleProp={(item) => item.cardStyle || {}}
        rightRenderCardActionIcons={renderRightCardActionIcons}
      />
      <CustomDialogBoxV2
        open={networkDialogOpen}
        onClose={handleCloseNetworkInterface}
        title={"Network Interface"}
        actionButtonStyle="continuous"
        successButtonName="Confirm"
        onClickSuccess={() => handleNetworkInterfaceSelect(selectedNetworkItem)}
        rejectButtonName={"Cancel"}
        onClickReject={handleCloseNetworkInterface}
        onDisabled={!selectedNetworkInterface}
      >
        <Box sx={{ width: "350px" }}>
          <Stack spacing={2}>
            {networkInterfacesEE?.length > 0 ? (
              <Stack>
                <CustomSelectV2
                  label={"Select The Interface"}
                  optionsList={networkInterfacesEE.map((ni) => ({
                    label: ni.name,
                    value: ni.name,
                  }))}
                  selectedValue={selectedNetworkInterface}
                  handleOnChange={(e) => {
                    setSelectedNetworkInterface(e.target.value);
                  }}
                  required={true}
                  placeholder={false}
                />
              </Stack>
            ) : (
              <Typography sx={{ ...componentStyle.font.cardText }}>
                No network interfaces available.
              </Typography>
            )}
          </Stack>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={dialogOpen}
        onClose={handleDialogClose}
        title={"Action"}
        actionButtonStyle="continuous"
        successButtonName={"Yes"}
        onClickSuccess={() => confirmUpdateAndRedirect()}
        onClickReject={updateChangesAndClose}
        rejectButtonName={"No"}
      >
        <Box sx={{ width: "350px", height: "50px" }}>
          <Typography>
            Are you sure the dependency linking of EE Component is completed?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={actionType2DialogOpen}
        onClose={handleActionType2DialogClose}
        title={"Action"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        onClickSuccess={handleRedirectionToTypeApproval}
        onClickReject={handleActionType2DialogClose}
        rejectButtonName={"Link Later"}
      >
        <Box sx={{ width: "350px", height: "50px" }}>
          <Typography>EE Component intersection linking is pending!</Typography>
        </Box>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={pendingEEActionDialogOpen}
        onClose={handlePendingEEActionDialogClose}
        title={"Action"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        onClickSuccess={handleRedirectionToEEComponents}
        onClickReject={handlePendingEEActionDialogClose}
        rejectButtonName={"Link Later"}
      >
        <Box sx={{ width: "350px", height: "50px" }}>
          <Typography>
            {Number(selectedPendingEEAction?.action_type) === 4
              ? "Action pending in EE Component Attribute"
              : "Action pending in EE Component Version"}
          </Typography>
        </Box>
      </CustomDialogBoxV2>
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleOpenNetworkInterface}
          onUnlinkRequest={handleEEComponentUnLink}
        />
      )}

      {openEELinkRecreateDialog && (
        <RecreateLinkDialog
          open={openEELinkRecreateDialog}
          onClose={closeRecreateDialog}
          entity={"EE"}
          linkList={recreateEELinkList}
          handleLink={handleOnClickRecreateLink}
        />
      )}
      <ModuleViewMoreDialog
        isEEDialogOpen={isEEDialogOpen}
        toggleEEDialog={toggleEEDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
        projectId={projectID}
        variantId={variantID}
        showSupplier={false}
      />
    </Box>
  );
}

EELinkPage.propTypes = {
  onBeforeSmartMatch: PropTypes.func,
};

export default EELinkPage;
